import { collection, doc, getDocs, getDoc, addDoc, updateDoc, serverTimestamp, query, orderBy, writeBatch, FieldValue } from 'firebase/firestore';
import { db } from './firebase';
import type { DiscipleshipClass, DiscipleshipClassFormData } from '../types/discipleship';

const classesCollectionRef = collection(db, 'discipleship-classes');

export const discipleshipService = {
  async getAllClasses(): Promise<DiscipleshipClass[]> {
    const q = query(classesCollectionRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { ...data, id: doc.id } as DiscipleshipClass;
    });
  },

  async getClassById(id: string): Promise<DiscipleshipClass | null> {
    const classDocRef = doc(db, 'discipleship-classes', id);
    const docSnap = await getDoc(classDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return { 
        ...data, 
        id: docSnap.id,
      } as DiscipleshipClass;
    } else {
      console.warn(`Class with ID ${id} not found.`);
      return null;
    }
  },

  async addClass(classData: DiscipleshipClassFormData): Promise<string> {
    const finalProgramName = classData.programName === 'other' ? classData.programNameOther : classData.programName;

    if (!finalProgramName && classData.programName === 'other') {
      // programName이 'other'인데 programNameOther가 비어있는 경우, 오류 처리 또는 기본값 설정
      throw new Error("새 과정 이름을 입력해주세요.");
    }
    
    // Firestore에 저장할 객체 타입 명시 (id 제외, createdAt은 serverTimestamp로 처리)
    const dataToSave: Omit<DiscipleshipClass, 'id' | 'createdAt'> & { createdAt: FieldValue } = {
      programName: finalProgramName!,
      session: classData.session,
      teacher: classData.teacher,
      durationInWeeks: Number(classData.durationInWeeks),
      createdAt: serverTimestamp(),
    };

    if (classData.programName === 'other' && classData.programNameOther) {
      dataToSave.programNameOther = classData.programNameOther;
    }
    // programName이 'other'가 아니면 programNameOther 필드는 dataToSave 객체에 포함되지 않음

    if (classData.startDate) {
      dataToSave.startDate = classData.startDate;
    }
    if (classData.description !== undefined) {
      dataToSave.description = classData.description;
    }

    const docRef = await addDoc(classesCollectionRef, dataToSave);
    return docRef.id;
  },

  async updateClass(id: string, updates: Partial<DiscipleshipClassFormData>): Promise<void> {
    const dataToUpdate: any = {};

    // programName 및 programNameOther 처리
    if (updates.programName !== undefined) {
      const newFinalProgramName = updates.programName === 'other' ? updates.programNameOther : updates.programName;
      dataToUpdate.programName = newFinalProgramName;

      if (updates.programName === 'other' && updates.programNameOther) {
        dataToUpdate.programNameOther = updates.programNameOther;
      } else if (updates.programName !== 'other') {
        // programName이 'other'가 아닌 것으로 변경되면 programNameOther를 null로 설정 (필드 제거)
        dataToUpdate.programNameOther = null; 
      }
    } else if (updates.hasOwnProperty('programNameOther')) {
      // programName 자체는 변경되지 않지만 programNameOther만 변경되는 경우 (기존 programName이 'other'라고 가정)
      dataToUpdate.programNameOther = updates.programNameOther;
    }
    
    // 다른 필드들 처리
    if (updates.hasOwnProperty('session')) dataToUpdate.session = updates.session;
    if (updates.hasOwnProperty('teacher')) dataToUpdate.teacher = updates.teacher;
    if (updates.hasOwnProperty('durationInWeeks')) dataToUpdate.durationInWeeks = Number(updates.durationInWeeks);
    
    if (updates.hasOwnProperty('startDate')) {
      dataToUpdate.startDate = updates.startDate || null; // 빈 문자열이면 null로 저장하여 필드 제거/초기화
    }
    if (updates.hasOwnProperty('description')) {
      // description이 명시적으로 GIVEN (undefined가 아닌) 경우에만 업데이트. 빈 문자열도 유효.
      // description을 null로 만들어 필드를 제거하고 싶다면, updates.description을 null로 전달해야 함.
      dataToUpdate.description = updates.description;
    }

    if (Object.keys(dataToUpdate).length === 0) {
      console.log("No actual changes to update for class: ", id);
      return; // 업데이트할 내용이 없으면 Firestore 쓰기 방지
    }
    
    const classDoc = doc(db, 'discipleship-classes', id);
    await updateDoc(classDoc, dataToUpdate);
  },

  async deleteClass(id: string): Promise<void> {
    const classDocRef = doc(db, 'discipleship-classes', id);
    const studentsCollectionRef = collection(db, 'discipleship-classes', id, 'students');
    const batch = writeBatch(db);

    try {
      const studentsQuerySnapshot = await getDocs(studentsCollectionRef);
      studentsQuerySnapshot.forEach(studentDoc => {
        batch.delete(studentDoc.ref);
      });
      batch.delete(classDocRef);
      await batch.commit();
      console.log(`Class ${id} and its students successfully deleted.`);
    } catch (error) {
      console.error(`Error deleting class ${id} and its students:`, error);
      throw error;
    }
  },
};
